import { useState } from 'react';
import { useQuery } from 'react-query';
import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { tableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { getAreasShortPaged } from 'services/areas-service';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { getUsersName } from 'services/users-service';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
import { InputLoader } from 'Components/PageLoader/Input';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { ModalConfirm } from 'Components/ModalConfirm';
import { callDeleteAction, callArchiveAction, callUnarchiveAction } from './actions';
import { PageHelper } from '../../Components/PageHelper';

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo à página de Áreas de Negócio! Aqui você pode gerenciar as diferentes áreas, setores ou squads da sua organização.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Este é o título da página. Clique aqui para retornar à página anterior.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Clique neste botão para adicionar uma nova área de negócio.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="info-section"]',
    content: 'Esta seção explica o propósito das áreas de negócio: mapear a organização da empresa para identificar pontos críticos.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filters-card"]',
    content: 'Use estes filtros para buscar áreas específicas por nome, criticidade, descrição, responsável ou status de arquivamento.',
  },
  {
    target: '[data-tour="filter-buttons"]',
    content: 'Use "Limpar" para resetar os filtros ou "Filtrar" para aplicar a busca.',
  },
  {
    target: '[data-tour="areas-table"]',
    content: 'Esta tabela exibe todas as áreas de negócio cadastradas. Você pode visualizar detalhes, editar, arquivar/desarquivar ou excluir áreas.',
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas de áreas quando houver muitos registros.',
  },
  {
    target: 'body',
    content: 'Tour concluído! Agora você pode gerenciar as áreas de negócio da sua organização.',
    placement: 'center',
  },
];

export const Areas = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      description: '',
      user: '',
      archived: false,
    },
  });

  const getContentSearch = () => {
    const dados = getValues();
    let queryString = `&archived=${dados.archived}`;
    if (dados.name !== '') {
      queryString += `&name=${dados.name}`;
    }
    if (dados.description !== '') {
      queryString += `&description=${dados.description}`;
    }
    if (dados.user !== '') {
      queryString += `&user=${dados.user}`;
    }
    if (dados.criticity !== '') {
      queryString += `&criticity=${dados.criticity}`;
    }

    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const {
    isLoading,
    error,
    data: pagedAreas,
  } = useQuery(['getAreas', refetch, page], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];

    const queryContent = getContentSearch();
    const result = await getAreasShortPaged(selectedTenant.slug, page, 15, queryContent);

    if (result?.data?.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setItemToDelete(null);
    setOpenDeleteModal(false);
  };

  const onArchive = item => {
    setItemToArchive(item);
    setOpenArchiveModal(true);
  };

  const onCloseArchiveModal = () => {
    setOpenArchiveModal(false);
    setItemToArchive(null);
  };

  const onUnarchive = item => {
    setItemToUnarchive(item);
    setOpenUnarchiveModal(true);
  };

  const onCloseUnarchiveModal = () => {
    setOpenUnarchiveModal(false);
    setItemToUnarchive(null);
  };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseDeleteModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseArchiveModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseUnarchiveModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <>
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="areas-tour"
        autoStart={true}
        showButton={true}
      />
      
      <Container title="Áreas de negócio" breadcrumb="Mapeamento / Áreas de negócio" showButtonAdd linkPage="/areas">
      <div data-tour="info-section">
        <Information
          title="Áreas de Negócios"
          description={
          <p className="m-0">
            Cada empresa possui uma organização distinta, seja por área, squad, setores, etc.
            <br />
            Nos demonstre como é a sua organização para que a gente possa identificar quais pontos críticos para o
            negócio.
            <br />
            Essas são compostas por diversos grupos e equipes que permitem o seu funcionamento adequado e que suas
            operações possam ser realizadas com sucesso.
          </p>
        }
        style={{
          marginTop: '24px',
        }}
      />
      </div>
      {!error && (
        <>
          <Card className="mb-2 mt-3" data-tour="filters-card">
            <Row className="mb-3" sx={{ alignItems: 'center', justifyContent: 'center' }}>
              <Column xs={12} md={4}>
                <CustomTextField
                  {...register('name')}
                  label="Nome"
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Column>
              <Column xs={12} md={3}>
                <Controller
                  control={control}
                  name="criticity"
                  render={({ field: { onChange, value } }) => {
                    const criticitiesData = [
                      { value: '1', color: '#4caf50', label: 'Muito Baixa' },
                      { value: '2', color: '#8bc34a', label: 'Baixa' },
                      { value: '3', color: '#ff9800', label: 'Média' },
                      { value: '4', color: '#ff5722', label: 'Alta' },
                      { value: '5', color: '#f44336', label: 'Muito Alta' }
                    ];
                    const selectedCriticity = criticitiesData.find(item => item.value === value);
                    
                    return (
                      <FormControl fullWidth>
                        <InputLabel id="criticity-label" shrink>
                          Criticidade
                        </InputLabel>
                        <Select
                          labelId="criticity-label"
                          id="criticity"
                          value={value}
                          label="Criticidade"
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                          renderValue={(selected) => {
                            if (!selected) return 'Selecione uma criticidade';
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: selectedCriticity?.color || '#ccc'
                                  }}
                                />
                                {selected} - {selectedCriticity?.label}
                              </div>
                            );
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            Selecione uma criticidade
                          </MenuItem>
                          {criticitiesData.map((item, index) => (
                            <MenuItem value={item.value} key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: item.color,
                                  marginRight: '8px'
                                }}
                              />
                              {item.value} - {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }}
                />
              </Column>
              <Column xs={12} md={3}>
                <CustomTextField
                  {...register('description')}
                  label="Descrição"
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Column>
              <Column xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Controller
                  control={control}
                  name="archived"
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      value={value}
                      control={<Checkbox checked={value} onChange={onChange} name="archived" />}
                      label="Arquivado"
                      sx={{ marginLeft: 0, whiteSpace: 'nowrap' }}
                    />
                  )}
                />
              </Column>
            </Row>
            <Row sx={{ alignItems: 'center', justifyContent: 'center' }}>
              <Column xs={12} md={4}>
                {!userLoading && (
                  <Controller
                    control={control}
                    name="user"
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="user-label" shrink>
                          Responsável
                        </InputLabel>
                        <Select
                          labelId="user-label"
                          id="user"
                          value={value}
                          label="Responsável"
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            Selecione um responsável
                          </MenuItem>
                          {users
                            ?.map(user => user.name)
                            .map((item, index) => (
                              <MenuItem value={item} key={`user-${index}`}>
                                {item}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
                {userLoading && (
                  <FormControl fullWidth sx={{ margin: '0 auto' }}>
                    <InputLabel id="user-label" shrink={true}>
                      Responsável
                    </InputLabel>
                    <InputLoader height={'40px'} />
                  </FormControl>
                )}
              </Column>
              <Column xs={12} md={8}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }} data-tour="filter-buttons">
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: '6px',
                    padding: '6px 12px',
                    textTransform: 'none',
                    width: '120px',
                    fontSize: '0.8rem',
                    height: '32px',
                  }}
                  onClick={() => {
                    reset();
                    setRefetch(!refetch);
                  }}
                >
                  Limpar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    borderRadius: '6px',
                    padding: '6px 12px',
                    textTransform: 'none',
                    width: '120px',
                    fontSize: '0.8rem',
                    height: '32px',
                  }}
                  endIcon={<SearchOutlinedIcon />}
                  disabled={watch('typeFilter') === ''}
                  onClick={() => {
                    setRefetch(!refetch);
                  }}
                >
                  Filtrar
                </Button>
                </div>
              </Column>
            </Row>
          </Card>
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>Ocorreu um erro ...</div>}
      {!isLoading && !error && pagedAreas.data.length === 0 && <NoData description={'Não há áreas para exibir'} />}
      {!isLoading && !error && pagedAreas.data?.length > 0 && (
        <div className="container">
          <div data-tour="areas-table">
            <DefaultTable
              rows={tableHeader}
              cells={pagedAreas.data}
              onClickDelete={onDelete}
              onClickArchive={onArchive}
              onClickUnarchive={onUnarchive}
              showArchive
              link="areas"
            />
          </div>
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }} data-tour="pagination">
            <Pagination
              count={pagedAreas.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir a Área de negócio <strong>{` ${itemToDelete?.name} `}</strong>?
              </>
            }
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <ModalConfirm
            description={
              <>
                Tem certeza que deseja arquivar a Área de negócio <strong>{` ${itemToArchive?.name}`}</strong>?
              </>
            }
            open={openArchiveModal}
            onClose={onCloseArchiveModal}
            onConfirm={onConfirmArchiveModal}
          />
          <ModalConfirm
            description={
              <>
                Tem certeza que deseja desarquivar a Área de negócio
                <strong>{` ${itemToUnarchive?.name}`}</strong>?
              </>
            }
            open={openUnarchiveModal}
            onClose={onCloseUnarchiveModal}
            onConfirm={onConfirmUnarchiveModal}
          />
        </div>
      )}
    </Container>
    </>
  );
};
